import axios from 'axios';
import { sitzungsKonfliktMelden, tabBenutzerSetzen } from '../utils/tabSitzung';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  // Default-Timeout 30s – verhindert hängende Requests, die das UI-State-Loading blockieren
  // würden. Lange Endpoints (Uploads, Reports) müssen ihren eigenen, höheren Timeout setzen.
  timeout: 30000,
  maxContentLength: Infinity,
  maxBodyLength: Infinity,
});

// MarketingManager als Projekttyp (Phase 2): Solange ein MarketingManager-Projekt geöffnet ist,
// gehen Anfragen an /marketing… und /campaigns… mit dem Header „X-Projekt-Id“ – das Backend ordnet Daten so dem
// Projekt zu. Ohne gesetztes Projekt nutzt das Backend das Standardprojekt des Mandanten.
let marketingProjektId = null;
export const setMarketingProjekt = (id) => { marketingProjektId = id || null; };

// Request Interceptor - Fügt Token hinzu (nur wenn noch keiner gesetzt ist)
api.interceptors.request.use(
  (config) => {
    const pfad = typeof config.url === 'string' ? config.url.replace(/^\/+/, '') : '';
    if (marketingProjektId && (pfad.startsWith('marketing') || pfad.startsWith('campaigns')) && !config.headers['X-Projekt-Id']) {
      config.headers['X-Projekt-Id'] = marketingProjektId;
    }
    // Nicht überschreiben, wenn bereits ein Authorization-Header gesetzt ist (z.B. von superadminApi)
    if (!config.headers.Authorization) {
      // Anmeldung im Browser gewechselt (anderer Tab)? Dann gehört diese Ansicht noch zum bisherigen
      // Konto – Anfrage nicht mit dem fremden Token senden, Tab sperren (utils/tabSitzung.js).
      if (sitzungsKonfliktMelden()) {
        const fehler = new Error('Anmeldung in diesem Browser gewechselt – Anfrage blockiert');
        fehler.code = 'ERR_CANCELED';
        return Promise.reject(fehler);
      }
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Flag um mehrfache Redirects zu verhindern
let isRedirecting = false;

// Liste von Endpunkten, bei denen 401-Fehler nicht zum Logout führen sollen
const SKIP_LOGOUT_ON_401 = [
  '/outlook/',
  '/oauth/',
  '/calendar/',
  '/outlook-addin/',
  '/superadmin/'
];

// Backend-Unavailable-Erkennung (während Deployment / Pod-Restart)
// Trigger: Network-Error oder 502/503/504 (Bad Gateway / Service Unavailable / Gateway Timeout).
// Ein Window-Event signalisiert das globale DeployOverlay.
const BACKEND_UNAVAILABLE_STATUSES = [502, 503, 504];
let lastUnavailableNotifyTs = 0;
const notifyBackendUnavailable = () => {
  const now = Date.now();
  // Throttle: max. 1 Event pro 1500ms
  if (now - lastUnavailableNotifyTs < 1500) return;
  lastUnavailableNotifyTs = now;
  try {
    // WICHTIG: erst alle registrierten Entwürfe synchron in localStorage sichern —
    // dispatchEvent läuft sync und wartet bis alle Listener zurück sind. Erst
    // DANACH das Overlay-Event feuern, damit die Meldung nicht erscheint
    // bevor die Arbeit gerettet ist.
    window.dispatchEvent(new CustomEvent('easywork:save-drafts', { detail: { reason: 'backend-unavailable' } }));
    window.dispatchEvent(new CustomEvent('easywork:backend-unavailable'));
  } catch (_e) { /* SSR-safe noop */ }
};

// Response Interceptor - Error Handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Prüfen ob es ein Outlook/OAuth-spezifischer Endpunkt ist
    const requestUrl = error.config?.url || '';
    const isOutlookEndpoint = SKIP_LOGOUT_ON_401.some(path => requestUrl.includes(path));

    // Backend nicht erreichbar (Deploy / Pod-Restart): Network-Error oder 502/503/504.
    // WICHTIG: ein reiner Client-Timeout (ECONNABORTED, z.B. eine einzelne langsame
    // Abfrage wie /email/templates in einem grossen Tenant) ist KEIN Beleg dafuer, dass
    // das Backend insgesamt down ist - das Overlay wuerde dann faelschlich das komplette
    // UI blockieren, obwohl der Aufrufer den Fehler oft schon selbst per .catch() abfaengt.
    // Nur ein echter Verbindungsfehler (Server nicht erreichbar) oder ein 502/503/504 vom
    // Server selbst gilt als "Backend down".
    const isTimeout = error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '');
    const isNetworkError = !error.response && !isTimeout && error.code !== 'ERR_CANCELED' && error.message !== 'canceled';
    // Ein 502/503/504 mit FastAPI-Fehlertext ({detail: …}) kommt vom laufenden Backend
    // selbst (z. B. Google/Outlook-API-Fehler) – das ist kein Deployment und darf
    // nicht das „Update“-Overlay auslösen. Nur Antworten ohne `detail` (nginx/Proxy).
    const hatBackendDetail = !!(error.response?.data && typeof error.response.data === 'object' && 'detail' in error.response.data);
    const isUnavailableStatus = error.response && BACKEND_UNAVAILABLE_STATUSES.includes(error.response.status) && !hatBackendDetail;
    if (isNetworkError || isUnavailableStatus) {
      notifyBackendUnavailable();
    }

    // Nur bei echten 401 Fehlern (nicht bei Netzwerkfehlern) und nicht bei Outlook-Endpunkten
    if (error.response?.status === 401 && !isRedirecting && !isOutlookEndpoint) {
      // Nicht umleiten wenn wir bereits auf der Login-Seite sind
      const currentPath = window.location.pathname;
      if (currentPath !== '/login' && currentPath !== '/' && currentPath !== '/register') {
        console.warn('Session abgelaufen - Umleitung zur Login-Seite');
        isRedirecting = true;
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        tabBenutzerSetzen(null); // eigene Abmeldung, kein Sitzungswechsel
        // Kurze Verzögerung um Race Conditions zu vermeiden
        setTimeout(() => {
          window.location.href = '/login';
          isRedirecting = false;
        }, 100);
      }
    }
    
    // Bei Outlook-401-Fehlern nur loggen, nicht ausloggen
    if (error.response?.status === 401 && isOutlookEndpoint) {
      console.warn('Outlook-Authentifizierungsfehler - Konto muss möglicherweise erneut verbunden werden');
    }
    
    return Promise.reject(error);
  }
);

export default api;
